// CampaignStatusBadge — the campaign-list status pill, including the
// due-date-derived label ("In Progress: 5 days remaining" / "Overdue: 3 days" /
// "Completed" / "Awaiting approval"). Extracted from CampaignList so the Home
// "Access Reviews" card renders the exact same badge.

import React, { memo } from "react";
import { Badge } from "@chakra-ui/react";
import { ployTheme } from "ui/theme";

interface CampaignStatusBadgeProps {
  status: string;
  dueDate: string;
  startedDate: string;
  /** Clip to the parent's width with an ellipsis instead of overflowing. Used
   *  by the Home card, where the badge sits in a fixed-width column and the
   *  verbose "In Progress: N days remaining" label would otherwise spill over
   *  the campaign name. The full-page list has room, so it leaves this off. */
  truncate?: boolean;
}

const CampaignStatusBadge: React.FC<CampaignStatusBadgeProps> = memo(
  // `startedDate` is part of the call shape (the campaign list passes it) but the
  // label is derived from status + dueDate only.
  ({ status, dueDate, truncate }) => {
    const getStatusColor = () => {
      switch (status) {
        case "completed":
          return { bg: "#D1FAE5", text: "#065F46" };
        case "in_progress":
          return { bg: "#E8EAFD", text: "#4349B8" };
        case "overdue":
          return { bg: "#FFF5F5", text: "#9B2C2C" };
        case "awaiting_approval":
          return {
            bg: ployTheme.colors.orange[100],
            text: ployTheme.colors.orange[700],
          };
        default:
          return { bg: "gray.50", text: "gray.700" };
      }
    };

    const colors = getStatusColor();

    // Days until due (negative = overdue).
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let label = status.replaceAll("_", " ");

    if (status === "awaiting_approval") {
      label = "Awaiting approval";
    } else if (status === "completed") {
      label = "Completed";
    } else if (status === "in_progress" && diffDays >= 0) {
      label = `In Progress: ${diffDays} day${diffDays !== 1 ? "s" : ""} remaining`;
    } else if (diffDays < 0) {
      label = `Overdue: ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? "s" : ""}`;
    }

    return (
      <Badge
        bg={colors.bg}
        color={colors.text}
        fontSize="14px"
        fontWeight="medium"
        textTransform="capitalize"
        px="6px"
        py="4px"
        borderRadius="6px"
        lineHeight="100%"
        // inline-block so the pill still hugs short labels, but caps at the
        // column width and ellipsises a long one rather than overflowing.
        {...(truncate && {
          display: "inline-block",
          maxW: "full",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        })}
      >
        {label}
      </Badge>
    );
  },
);

CampaignStatusBadge.displayName = "CampaignStatusBadge";

export default CampaignStatusBadge;
